import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';

import Utils, { UtilsValidators } from 'src/app/helpers/utils';
import {
  FUND_ACTIVATION_REASON_LABELS,
  FUND_INACTIVATION_REASON_LABELS,
  FundActivationReason,
  FundInactivationReason,
  FundInactivationView,
  FundTransferData,
  getInactivationReasonLabel,
  INTERNAL_REASON_TO_API,
  isFutureTransferDate,
  mapFundInactivationFromFund,
  parseDisplayDate,
  getTransferDestinationDisplay,
} from 'src/app/helpers/fund-operational-status';
import {
  FundInactivationPreviewRegister,
  FundInactivationPreviewResponse,
  FundInactivationPreviewView,
  FundOperationalStatusService,
  FundInactivationPayload,
  FundReactivationPayload,
  mapInactivationPreview,
} from 'src/app/services/fund-operational-status/fund-operational-status.service';
import { ToastService } from 'src/app/services/toast/toast.service';

export type FundOperationalModalMode = 'inactivate' | 'activate' | null;

export type FundOperationalPrefill = {
  type?: string;
  transferDate?: string;
  assemblyDate?: string;
  observation?: string;
  reasonKind?: string;
  hasAdministratorFields?: boolean;
  administrator?: {
    razaoSocial?: string;
    cnpj?: string;
    contato?: string;
    email?: string;
    telefone?: string;
    celular?: string;
  };
};

@Component({
  selector: 'app-fund-operational-status-modal',
  templateUrl: './fund-operational-status-modal.component.html',
  styleUrls: ['./fund-operational-status-modal.component.scss'],
})
export class FundOperationalStatusModalComponent
  implements OnChanges, OnDestroy
{
  @Input() fund: any = null;
  @Input() mode: FundOperationalModalMode = null;
  @Input() prefill: FundOperationalPrefill | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() statusChanged = new EventEmitter<void>();

  submitting = false;
  previewLoading = false;
  preview: FundInactivationPreviewResponse | null = null;
  previewView: FundInactivationPreviewView | null = null;
  confirmDisabled = true;
  showImmediateConfirmModal = false;
  showImpactListModal = false;
  impactListTab: 'assignors' | 'shareholders' = 'assignors';
  private formSubscription?: Subscription;
  private reasonSubscription?: Subscription;

  private readonly transferDateValidators: ValidatorFn[] = [
    Validators.required,
    Utils.validateMinDate,
    Utils.validateMaxDate,
  ];

  readonly getTransferDestinationDisplay = getTransferDestinationDisplay;

  readonly reasonItems: SelectItem[] = (
    Object.entries(FUND_INACTIVATION_REASON_LABELS) as Array<
      [FundInactivationReason, string]
    >
  ).map(([value, label]) => ({ label, value }));

  readonly activateReasonItems: SelectItem[] = (
    Object.entries(FUND_ACTIVATION_REASON_LABELS) as Array<
      [FundActivationReason, string]
    >
  ).map(([value, label]) => ({ label, value }));

  readonly inactivateForm = this.formBuilder.group({
    reason: ['transfer' as FundInactivationReason, [Validators.required]],
    justification: [''],
    transfer_date: [''],
    assembly_date: [''],
    cnpj: [''],
    company_name: [''],
    contact_name: [''],
    email: [''],
    phone_ddi: ['+55'],
    phone: [''],
    mobile_ddi: ['+55'],
    mobile: [''],
  });

  readonly activateForm = this.formBuilder.group({
    reason: ['transfer' as FundActivationReason, [Validators.required]],
    justification: [''],
    transfer_date: [''],
    assembly_date: [''],
    cnpj: [''],
    company_name: [''],
    contact_name: [''],
    email: [''],
    phone_ddi: ['+55'],
    phone: [''],
    mobile_ddi: ['+55'],
    mobile: [''],
  });

  constructor(
    private formBuilder: FormBuilder,
    private statusService: FundOperationalStatusService,
    private toast: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  get inactivationTitle(): string {
    return this.isEditingInactivation ? 'Editar inativação' : 'Inativar fundo';
  }

  get inactivationConfirmText(): string {
    return this.isEditingInactivation
      ? 'Salvar alterações'
      : 'Confirmar inativação';
  }

  get loadingText(): string {
    if (this.mode === 'activate') return 'Solicitando reativação...';
    if (this.isEditingInactivation && this.isImmediateInactivation) {
      return 'Inativando fundo...';
    }
    if (this.isEditingInactivation) return 'Atualizando inativação...';
    if (this.mode === 'inactivate') return 'Inativando fundo...';
    return 'Processando...';
  }

  get loadingConfirmText(): string {
    if (this.mode === 'activate') return 'Solicitando...';
    if (this.isEditingInactivation && this.isImmediateInactivation) {
      return 'Inativando...';
    }
    if (this.isEditingInactivation) return 'Salvando...';
    if (this.mode === 'inactivate') return 'Inativando...';
    return 'Processando...';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.mode) return;
    if (!changes.mode && !changes.prefill) return;

    this.resetForms();
    this.applyPrefill();
    this.bindCurrentForm();
    this.showImmediateConfirmModal = false;
    this.showImpactListModal = false;
    this.impactListTab = 'assignors';
    if (this.mode === 'inactivate') {
      this.loadPreview();
    }
  }

  ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
    this.reasonSubscription?.unsubscribe();
  }

  get fundName(): string {
    return this.fund?.name ?? this.fund?.fantasy_name ?? '-';
  }

  get fundDocument(): string {
    const document = String(
      this.fund?.document ?? this.fund?.document_number ?? ''
    ).replace(/[^\dA-Za-z]/g, '');
    if (document.length !== 14) return document || '-';
    return document.replace(
      /^(.{2})(.{3})(.{3})(.{4})(.{2})$/,
      '$1.$2.$3/$4-$5'
    );
  }

  get currentInactivation(): FundInactivationView | null {
    return mapFundInactivationFromFund(this.fund);
  }

  isTransferReason(): boolean {
    return this.inactivateForm.get('reason')?.value === 'transfer';
  }

  isActivationTransferReason(): boolean {
    return this.activateForm.get('reason')?.value === 'transfer';
  }

  get isEditingInactivation(): boolean {
    return this.mode === 'inactivate' && Boolean(this.prefill);
  }

  get transferDateDisplay(): string {
    return this.trimmed(this.inactivateForm, 'transfer_date');
  }

  get transferDateTiming(): 'future' | 'today' | 'past' | null {
    if (this.mode !== 'inactivate' || !this.isTransferReason()) return null;

    const value = this.transferDateDisplay;
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return null;

    const [day, month, year] = value.split('/').map(Number);
    const selected = new Date(year, month - 1, day);
    if (
      selected.getFullYear() !== year ||
      selected.getMonth() !== month - 1 ||
      selected.getDate() !== day
    ) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selected.setHours(0, 0, 0, 0);

    const selectedTime = selected.getTime();
    const todayTime = today.getTime();

    if (selectedTime === todayTime) return 'today';
    if (selectedTime < todayTime) return 'past';
    return 'future';
  }

  get isImmediateInactivation(): boolean {
    if (this.mode !== 'inactivate') return false;
    if (!this.isTransferReason()) return true;
    return (
      this.transferDateTiming === 'today' || this.transferDateTiming === 'past'
    );
  }

  get impactListItems(): FundInactivationPreviewRegister[] {
    if (!this.previewView) return [];
    return this.impactListTab === 'assignors'
      ? this.previewView.inactivatedAssignors
      : this.previewView.inactivatedShareholders;
  }

  get hasInactivationImpactList(): boolean {
    const impact = this.previewView;
    if (!impact) return false;
    return (
      impact.inactivatedAssignorsCount > 0 ||
      impact.inactivatedShareholdersCount > 0
    );
  }

  reasonLabel(reason?: FundInactivationReason | string | null): string {
    return getInactivationReasonLabel(reason, this.currentInactivation?.reasonLabel);
  }

  inactivationJustification(): string {
    const inactivation = this.currentInactivation;
    if (!inactivation) return '-';
    if (inactivation.reason === 'transfer') {
      return inactivation.comment?.trim() || 'Transferência registrada';
    }
    return inactivation.comment?.trim() || '-';
  }

  formatPreviewDocument(documento: string): string {
    if (!documento || documento === '-') return '-';
    const digits = documento.replace(/\D/g, '');
    if (digits.length === 11) {
      return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    }
    if (digits.length === 14) {
      return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5'
      );
    }
    return documento;
  }

  close(): void {
    if (this.submitting) return;
    this.showImmediateConfirmModal = false;
    this.showImpactListModal = false;
    this.impactListTab = 'assignors';
    this.formSubscription?.unsubscribe();
    this.formSubscription = undefined;
    this.preview = null;
    this.previewView = null;
    this.closed.emit();
  }

  closeImmediateConfirmModal(): void {
    if (this.submitting) return;
    this.showImmediateConfirmModal = false;
  }

  openImpactListModal(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.impactListTab = 'assignors';
    this.showImpactListModal = true;
  }

  closeImpactListModal(): void {
    this.showImpactListModal = false;
  }

  setImpactListTab(tab: 'assignors' | 'shareholders'): void {
    this.impactListTab = tab;
  }

  async confirmInactivation(): Promise<void> {
    this.updateInactivationValidators();

    if (!this.inactivateForm.valid) {
      this.inactivateForm.markAllAsTouched();
      this.syncConfirmDisabled();
      this.cdr.detectChanges();
      return;
    }

    if (!this.fund?.id) return;

    if (this.isImmediateInactivation) {
      this.showImmediateConfirmModal = true;
      this.cdr.detectChanges();
      return;
    }

    await this.submitInactivation();
  }

  async confirmImmediateInactivation(): Promise<void> {
    await this.submitInactivation();
  }

  private async submitInactivation(): Promise<void> {
    const reason = this.inactivateForm.get('reason')
      ?.value as FundInactivationReason;
    const comment =
      this.trimmed(this.inactivateForm, 'justification') ||
      (reason === 'transfer'
        ? 'Transferência operacional'
        : FUND_INACTIVATION_REASON_LABELS[reason]);
    const transfer =
      reason === 'transfer' ? this.buildTransferData(this.inactivateForm) : undefined;
    const isScheduled =
      reason === 'transfer' && isFutureTransferDate(transfer?.transferDate);

    const payload: FundInactivationPayload = {
      confirm: true,
      reason: reason === 'transfer' ? 'transfer' : INTERNAL_REASON_TO_API[reason],
      comment,
    };

    if (transfer) {
      payload.transfer = { entry: this.buildInactivationEntry(transfer) };
      if (isScheduled) {
        payload.scheduled_for = this.toScheduledFor(transfer.transferDate);
      }
    }

    this.submitting = true;
    this.syncConfirmDisabled();
    this.cdr.detectChanges();
    try {
      if (this.isEditingInactivation) {
        const transferData = this.isTransferReason()
          ? transfer ?? this.buildTransferData(this.inactivateForm)
          : {
              ...this.buildTransferData(this.inactivateForm),
              transferDate: this.getTodayDisplayDate(),
            };

        await this.statusService.updateScheduledInactivationTransfer(
          this.fund.id,
          {
            comment,
            transfer: {
              entry: this.buildInactivationEntry(transferData),
            },
          }
        );

        if (this.isImmediateInactivation) {
          await this.statusService.confirmScheduledInactivation(this.fund.id);
        }

        this.statusChanged.emit();
        this.toast.show(
          'info',
          'Sucesso',
          this.isImmediateInactivation
            ? 'Fundo inativado com sucesso.'
            : 'Transferência agendada atualizada com sucesso.'
        );
        this.closeAfterSubmit();
        return;
      }

      await this.statusService.inactivateFund(this.fund.id, payload);

      this.statusChanged.emit();
      this.toast.show(
        'info',
        'Sucesso',
        isScheduled
          ? 'Inativação agendada com sucesso.'
          : 'Fundo inativado com sucesso.'
      );
      this.closeAfterSubmit();
    } catch (error) {
      this.showOperationError(error, 'inativar');
    } finally {
      this.submitting = false;
    }
  }

  async confirmActivation(): Promise<void> {
    this.updateActivationValidators();

    if (!this.activateForm.valid) {
      this.activateForm.markAllAsTouched();
      this.syncConfirmDisabled();
      this.cdr.detectChanges();
      return;
    }

    if (!this.fund?.id) return;

    const reason = this.activateForm.get('reason')
      ?.value as FundActivationReason;
    const comment =
      this.trimmed(this.activateForm, 'justification') ||
      (reason === 'transfer'
        ? 'Reativação por transferência administrativa.'
        : FUND_ACTIVATION_REASON_LABELS[reason]);
    const payload: FundReactivationPayload = { reason, comment };

    if (reason === 'transfer') {
      payload.transfer = { entry: this.buildReactivationEntry() };
    }

    this.submitting = true;
    this.syncConfirmDisabled();
    this.cdr.detectChanges();
    try {
      await this.statusService.reactivateFund(this.fund.id, payload);

      this.statusChanged.emit();
      this.toast.show(
        'info',
        'Sucesso',
        'Reativação solicitada com sucesso.'
      );
      this.closeAfterSubmit();
    } catch (error) {
      this.showOperationError(error, 'reativar');
    } finally {
      this.submitting = false;
    }
  }

  private async loadPreview(): Promise<void> {
    if (!this.fund?.id) return;
    this.previewLoading = true;
    this.preview = null;
    this.previewView = null;
    try {
      this.preview = await this.statusService.getInactivationPreview(
        this.fund.id
      );
      this.previewView = mapInactivationPreview(this.preview);
    } catch (error) {
      this.preview = null;
      this.previewView = null;
      this.toast.show(
        'error',
        'Erro',
        'Não foi possível consultar o impacto da inativação.'
      );
    } finally {
      this.previewLoading = false;
    }
  }

  private bindCurrentForm(): void {
    this.formSubscription?.unsubscribe();
    this.reasonSubscription?.unsubscribe();

    const form =
      this.mode === 'inactivate' ? this.inactivateForm : this.activateForm;

    if (this.mode === 'inactivate') {
      this.updateInactivationValidators();
      this.reasonSubscription = this.inactivateForm
        .get('reason')
        ?.valueChanges.subscribe(() => {
          this.updateInactivationValidators();
          this.syncConfirmDisabled();
        });
    }

    if (this.mode === 'activate') {
      this.updateActivationValidators();
      this.reasonSubscription = this.activateForm
        .get('reason')
        ?.valueChanges.subscribe(() => {
          this.updateActivationValidators();
          this.syncConfirmDisabled();
        });
    }

    this.formSubscription = form.valueChanges.subscribe(() =>
      this.syncConfirmDisabled()
    );
    this.syncConfirmDisabled();
  }

  private syncConfirmDisabled(): void {
    if (this.submitting) {
      this.confirmDisabled = true;
      return;
    }
    this.confirmDisabled =
      this.mode === 'inactivate'
        ? !this.isInactivateFormValid()
        : !this.isActivateFormValid();
  }

  private isInactivateFormValid(): boolean {
    return this.inactivateForm.valid;
  }

  private updateInactivationValidators(): void {
    this.updateAdministratorFieldValidators(
      this.inactivateForm,
      this.inactivateForm.get('reason')?.value === 'transfer'
    );
  }

  private updateActivationValidators(): void {
    const isTransfer = this.isActivationTransferReason();
    const justification = this.activateForm.get('justification');

    if (isTransfer) {
      justification?.clearValidators();
      justification?.disable({ emitEvent: false });
      this.setControlValidators(this.activateForm, 'cnpj', [
        Validators.required,
        UtilsValidators.cnpj,
      ]);
      this.setControlValidators(this.activateForm, 'company_name', [
        Validators.required,
        UtilsValidators.notOnlyWhitespace,
      ]);
      this.setControlValidators(this.activateForm, 'email', [
        Validators.required,
        UtilsValidators.email,
      ]);
      this.setControlValidators(this.activateForm, 'phone', [
        Utils.validateWhenNotEmpty(UtilsValidators.telefoneFixo),
      ]);
      this.setControlValidators(this.activateForm, 'mobile', [
        Utils.validateWhenNotEmpty(UtilsValidators.celular),
      ]);
      this.activateForm.setValidators(null);
    } else {
      justification?.enable({ emitEvent: false });
      this.clearTransferValidators(this.activateForm);
      this.setControlValidators(this.activateForm, 'justification', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(500),
      ]);
      this.activateForm.setValidators(null);
    }

    this.activateForm.updateValueAndValidity({ emitEvent: false });
  }

  private updateAdministratorFieldValidators(
    form: FormGroup,
    showAdministratorFields: boolean
  ): void {
    if (showAdministratorFields) {
      this.setControlValidators(form, 'transfer_date', this.transferDateValidators);
      this.setControlValidators(form, 'assembly_date', this.transferDateValidators);
      this.setControlValidators(form, 'cnpj', [
        Validators.required,
        UtilsValidators.cnpj,
      ]);
      this.setControlValidators(form, 'company_name', [
        Validators.required,
        UtilsValidators.notOnlyWhitespace,
      ]);
      this.setControlValidators(form, 'contact_name', [
        Utils.validateWhenNotEmpty(UtilsValidators.fullName),
      ]);
      this.setControlValidators(form, 'email', [
        Utils.validateWhenNotEmpty(UtilsValidators.email),
      ]);
      this.setControlValidators(form, 'phone', [
        Utils.validateWhenNotEmpty(UtilsValidators.telefoneFixo),
      ]);
      this.setControlValidators(form, 'mobile', [
        Utils.validateWhenNotEmpty(UtilsValidators.celular),
      ]);
      this.setControlValidators(form, 'justification', []);
      return;
    }

    this.clearTransferValidators(form);
    this.setControlValidators(form, 'justification', [
      Validators.required,
      Validators.minLength(10),
    ]);
  }

  private clearTransferValidators(form: FormGroup): void {
    [
      'transfer_date',
      'assembly_date',
      'cnpj',
      'company_name',
      'contact_name',
      'email',
      'phone',
      'mobile',
    ].forEach((controlName) => this.setControlValidators(form, controlName, []));
  }

  private setControlValidators(
    form: FormGroup,
    controlName: string,
    validators: ValidatorFn[]
  ): void {
    const control = form.get(controlName);
    if (!control) return;
    control.setValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private isActivateFormValid(): boolean {
    if (this.isActivationTransferReason()) {
      const requiredFields = ['cnpj', 'company_name', 'email'];
      if (
        requiredFields.some((name) => this.activateForm.get(name)?.invalid)
      ) {
        return false;
      }

      return !(
        this.activateForm.get('phone')?.invalid ||
        this.activateForm.get('mobile')?.invalid
      );
    }

    return Boolean(this.activateForm.get('justification')?.valid);
  }

  private getOperationForm() {
    return this.mode === 'activate' ? this.activateForm : this.inactivateForm;
  }

  private buildTransferData(form: FormGroup): FundTransferData {
    return {
      transferDate: this.trimmed(form, 'transfer_date'),
      assemblyDate: this.trimmed(form, 'assembly_date'),
      cnpj: this.trimmed(form, 'cnpj'),
      companyName: this.trimmed(form, 'company_name'),
      contactName: this.trimmed(form, 'contact_name'),
      email: this.trimmed(form, 'email'),
      phoneDdi: this.trimmed(form, 'phone_ddi') || '+55',
      phone: this.trimmed(form, 'phone'),
      mobileDdi: this.trimmed(form, 'mobile_ddi') || '+55',
      mobile: this.trimmed(form, 'mobile'),
    };
  }

  private buildInactivationEntry(transfer: FundTransferData) {
    const phone = this.normalizePhoneNumber(transfer.phone);
    const mobile = this.normalizePhoneNumber(transfer.mobile);

    return {
      transferDate: this.toIsoDate(transfer.transferDate) || transfer.transferDate,
      assemblyDate: this.toIsoDate(transfer.assemblyDate) || transfer.assemblyDate || null,
      companyDocument: transfer.cnpj,
      companyName: transfer.companyName,
      email: transfer.email,
      ...(phone ? { phoneNumber: phone } : {}),
      ...(mobile ? { mobileNumber: mobile } : {}),
    };
  }

  private toIsoDate(displayDate?: string | null): string | null {
    const date = parseDisplayDate(displayDate);
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toScheduledFor(displayDate?: string | null): string | undefined {
    const isoDate = this.toIsoDate(displayDate);
    if (!isoDate) return undefined;
    return `${isoDate}T09:00:00-03:00`;
  }

  private getTodayDisplayDate(): string {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${today.getFullYear()}`;
  }

  private buildReactivationEntry() {
    const phone = this.normalizePhoneNumber(
      this.trimmed(this.activateForm, 'phone')
    );
    const mobile = this.normalizePhoneNumber(
      this.trimmed(this.activateForm, 'mobile')
    );

    return {
      companyDocument: this.trimmed(this.activateForm, 'cnpj'),
      companyName: this.trimmed(this.activateForm, 'company_name'),
      email: this.trimmed(this.activateForm, 'email'),
      ...(phone ? { phoneNumber: phone } : {}),
      ...(mobile ? { mobileNumber: mobile } : {}),
    };
  }

  private resetForms(): void {
    const emptyAdministratorFields = {
      justification: '',
      transfer_date: '',
      assembly_date: '',
      cnpj: '',
      company_name: '',
      contact_name: '',
      email: '',
      phone_ddi: '+55',
      phone: '',
      mobile_ddi: '+55',
      mobile: '',
    };

    this.inactivateForm.reset({
      reason: 'transfer',
      ...emptyAdministratorFields,
    });
    this.activateForm.reset({
      reason: 'transfer',
      ...emptyAdministratorFields,
    });
    this.updateInactivationValidators();
    this.updateActivationValidators();
  }

  private applyPrefill(): void {
    if (!this.prefill) return;

    const form = this.getOperationForm();
    const reason = this.mapPrefillReason(this.prefill.reasonKind);
    const phone = this.parsePhoneDisplay(this.prefill.administrator?.telefone);
    const mobile = this.parsePhoneDisplay(this.prefill.administrator?.celular);

    form.patchValue(
      {
        reason,
        justification: this.emptyIfPlaceholder(this.prefill.observation),
        transfer_date: this.emptyIfPlaceholder(this.prefill.transferDate),
        assembly_date: this.emptyIfPlaceholder(this.prefill.assemblyDate),
        cnpj: this.emptyIfPlaceholder(this.prefill.administrator?.cnpj),
        company_name: this.emptyIfPlaceholder(
          this.prefill.administrator?.razaoSocial
        ),
        contact_name: this.emptyIfPlaceholder(
          this.prefill.administrator?.contato
        ),
        email: this.emptyIfPlaceholder(this.prefill.administrator?.email),
        phone_ddi: phone.ddi,
        phone: phone.number,
        mobile_ddi: mobile.ddi,
        mobile: mobile.number,
      },
      { emitEvent: false }
    );

    if (this.mode === 'inactivate') this.updateInactivationValidators();
    if (this.mode === 'activate') this.updateActivationValidators();
  }

  private mapPrefillReason(
    reasonKind?: string
  ): FundInactivationReason | FundActivationReason {
    if (this.mode === 'activate') {
      return reasonKind === 'receipt' || reasonKind === 'transfer'
        ? 'transfer'
        : 'other';
    }

    if (reasonKind === 'transfer') return 'transfer';
    if (reasonKind === 'closure') return 'closure';
    return 'other';
  }

  private emptyIfPlaceholder(value?: string | null): string {
    const normalized = String(value ?? '').trim();
    return !normalized || normalized === '--' || normalized === '-'
      ? ''
      : normalized;
  }

  private parsePhoneDisplay(value?: string | null): {
    ddi: string;
    number: string;
  } {
    const empty = { ddi: '+55', number: '' };
    const normalized = this.emptyIfPlaceholder(value);
    if (!normalized) return empty;

    const match = normalized.match(/^(\+\d{1,4})\s*(.*)$/);
    if (!match) {
      return { ddi: '+55', number: normalized.replace(/\D/g, '') };
    }

    return {
      ddi: match[1],
      number: match[2].replace(/\D/g, ''),
    };
  }

  private trimmed(form: { get: (name: string) => any }, name: string): string {
    return String(form.get(name)?.value ?? '').trim();
  }

  private normalizePhoneNumber(value?: string | null): string {
    return String(value ?? '').replace(/\D/g, '');
  }

  private closeAfterSubmit(): void {
    this.submitting = false;
    this.close();
  }

  private showOperationError(error: any, operation: string): void {
    const status = error?.status;
    const message =
      status === 404
        ? 'Fundo não encontrado.'
        : status === 409
        ? `O fundo já está ${operation === 'inativar' ? 'inativo' : 'ativo'}.`
        : error?.error?.detail ||
          `Não foi possível ${operation} o fundo. Tente novamente.`;
    this.toast.show('error', 'Erro', message);
  }
}
